import {
  compareTraceOutputs,
  isTraceValuesOutput,
  normalizeTraceOutput,
} from "@/metrics/runtime/record-data"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import type { NumericalMetricRecord } from "./base/NumericalMetricRecord"
import { SetMetricRecord } from "./base/SetMetricRecord"
import {
  traceComparedOverview,
  traceInDepthReview,
  traceNumericRecord,
  traceOverview,
} from "./helpers/CustomTraceOutput"
import {
  CustomTraceValues,
  CustomTraceValuesSummary,
} from "./helpers/CustomTraceValues"
import {
  renderCustomTraceEditorMetadata,
  renderCustomTraceMetadataField,
} from "./numerical/CustomTraceMetricRecord"

export class CustomTraceValuesMetricRecord extends SetMetricRecord {
  private readonly sourceData: MetricRecordData

  constructor(data: MetricRecordData) {
    super(data)
    this.sourceData = data
  }
  renderEditorMetadata = renderCustomTraceEditorMetadata
  renderMetadataField = renderCustomTraceMetadataField

  renderMetricType(): React.ReactNode {
    return "Custom Trace Metric"
  }

  renderBadge(
    ...[output, rerun]: Parameters<SetMetricRecord["renderBadge"]>
  ): React.ReactNode {
    return super.renderBadge(normalizeTraceOutput(output), rerun)
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    if (!isTraceValuesOutput(mo)) {
      return (
        <>
          <span className="text-coval-text-muted">
            Previous numeric result:{" "}
          </span>
          {traceNumericRecord(this.sourceData, [mo])._renderSimulationCard(mo)}
        </>
      )
    }
    return <CustomTraceValuesSummary mo={mo} />
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    if (!isTraceValuesOutput(mo)) {
      return (
        <>
          <p className="text-sm text-coval-text-muted">
            Numeric result from an earlier metric configuration.
          </p>
          {traceNumericRecord(this.sourceData, [mo]).getSingleMetricOutputNode(
            mo,
          )}
        </>
      )
    }
    return <CustomTraceValues mo={mo} />
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode {
    return this._renderDeepDive(mo)
  }

  compareValues(...values: Parameters<typeof compareTraceOutputs>): boolean {
    return compareTraceOutputs(...values)
  }

  getComparedOverviewNode(
    ...[runRows, compareBy]: Parameters<
      SetMetricRecord["getComparedOverviewNode"]
    >
  ): React.ReactNode {
    return traceComparedOverview(
      this.sourceData,
      this._getMetricOutputsFromRunRows(runRows),
      runRows,
      compareBy,
    )
  }

  renderInDepthRunReview(
    ...[outputs, context]: Parameters<SetMetricRecord["renderInDepthRunReview"]>
  ): React.ReactNode {
    return traceInDepthReview(this.sourceData, outputs, context)
  }

  renderComparedInDepthRunReview(
    ...[outputs, runRows, compareBy]: Parameters<
      SetMetricRecord["renderComparedInDepthRunReview"]
    >
  ): React.ReactNode {
    return traceComparedOverview(this.sourceData, outputs, runRows, compareBy)
  }

  hasValidOutputValue(mo: RunMetricOutput): boolean {
    return isTraceValuesOutput(mo)
      ? Array.isArray(mo.set_value)
      : mo.float_value != null && Number.isFinite(mo.float_value)
  }

  getOverviewNode(
    mos: RunMetricOutput[],
    options?: Parameters<NumericalMetricRecord["getOverviewNode"]>[1],
  ): React.ReactNode {
    return traceOverview(this.sourceData, mos, options)
  }
}
